import * as React from "react";
import { BusinessReportOutcomeHeader } from "./businessReportOutcomeHeader";
import { BusinessReportOutcomeHeaderMenu } from "./businessReportOutcomeHeaderMenu";
import { BusinessReportOutcomeInit } from "./businessReportOutcomeInit";
import { BusinessReportOutcomeExport } from "./export";
import { BusinessReportOutcomeSaveToCollection } from "@/modules/snapshots/outcome-details/saveToCollection";
import { BusinessReportOutcomeVerify } from "@/modules/snapshots/outcome-details/verify";
import { Button } from "@/components/ui/button";
import { Ban } from "lucide-react";
import { useCancelBusinessReportOutcomeMutation } from "@/modules/snapshots/businessReports.api";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import {
  isBusinessReportOutcome,
  isFamilySummaryOutcome,
  type BusinessReportOutcomeDetails,
} from "../businessReports.types";
import { PromptResult } from "@/modules/snapshots/outcome-details/businessReportOutcomeContent";
import type { Evidence } from "@/types/evidences";
import { useNavigate } from "react-router";
import { ReportPanelType } from "./types";

const PANEL_TOGGLES: ReadonlyArray<{ panel: Exclude<ReportPanelType, null>; label: string }> = [
  { panel: "pdf", label: "PDF" },
  { panel: "improve", label: "Improve" },
  { panel: "library", label: "Library" },
];

interface Props {
  businessReportOutcome: BusinessReportOutcomeDetails | null;
  isComplete: boolean;
  inProgress: boolean;
  setPromptsResult: React.Dispatch<React.SetStateAction<PromptResult[]>>;
  setEvidences: React.Dispatch<React.SetStateAction<Evidence[]>>;
  onCreateEventClick: () => void;
  hasEvent: boolean;
  businessReportOutcomeId: string;
  isPanelOpen: boolean;
  onPanelToggle: (panel: Exclude<ReportPanelType, null>) => void;
}

export const BusinessReportOutcomeHeaderSection: React.FC<Props> = ({
  businessReportOutcome,
  isComplete,
  inProgress,
  setPromptsResult,
  setEvidences,
  hasEvent,
  businessReportOutcomeId,
  onCreateEventClick,
  isPanelOpen,
  onPanelToggle,
}) => {
  const navigate = useNavigate();
  const user = useAuthenticatedUser();
  const [cancelOutcome] = useCancelBusinessReportOutcomeMutation();

  const isBusinessReport = isBusinessReportOutcome(businessReportOutcome);
  const isFamilySummary = isFamilySummaryOutcome(businessReportOutcome);
  const isOwner = !!businessReportOutcome?.permissions.some(
    permission => permission.user_id === user.id && permission.role === "owner",
  );
  const canViewEvent = !isBusinessReport && hasEvent;
  const canCreateEvent =
    !isBusinessReport && !isFamilySummary && !hasEvent && isComplete && !!businessReportOutcome?.snapshot_collection_id;

  const handleCancel = () => {
    if (businessReportOutcome) {
      cancelOutcome({ id: businessReportOutcome.id });
    }
  };

  const handleViewEvents = () => {
    navigate(`/snapshot-events?businessReportOutcomeId=${businessReportOutcomeId}`);
  };

  return (
    <header className="mb-5 mt-4 flex flex-wrap items-start justify-between gap-x-5 gap-y-3 border-l-[5px] border-[#c9b39c] pl-5">
      <div className="min-w-0 flex-1 basis-72">
        <BusinessReportOutcomeHeader businessReportOutcome={businessReportOutcome} />
        <BusinessReportOutcomeInit
          businessReportOutcome={businessReportOutcome}
          setPromptsResult={setPromptsResult}
          setEvidences={setEvidences}
          inProgress={inProgress}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="SnapShot actions">
        {inProgress && businessReportOutcome && (
          <Button type="button" onClick={handleCancel} variant="outline" size="sm">
            <Ban className="mr-1.5 size-4" />
            Cancel
          </Button>
        )}
        {!isBusinessReport && isComplete && businessReportOutcome && (
          <BusinessReportOutcomeVerify businessReportOutcome={businessReportOutcome} />
        )}
        {isComplete && businessReportOutcome && (
          <BusinessReportOutcomeExport businessReportOutcomeId={businessReportOutcome.id} />
        )}
        {isComplete && businessReportOutcome && !businessReportOutcome.snapshot_collection_id && (
          <BusinessReportOutcomeSaveToCollection businessReportOutcome={businessReportOutcome} />
        )}
        {!isBusinessReport && !isPanelOpen && (
          <fieldset className="flex h-9 rounded-md border border-[#d8cfc5] bg-white p-1">
            <legend className="sr-only">Side panel</legend>
            {PANEL_TOGGLES.map(({ panel, label }) => (
              <button
                key={panel}
                type="button"
                className="h-full rounded px-3 text-sm font-semibold text-gray-600 transition-colors hover:bg-[#f7f2ec] hover:text-[#111b35]"
                onClick={() => onPanelToggle(panel)}
              >
                {label}
              </button>
            ))}
          </fieldset>
        )}
        {businessReportOutcome && (
          <BusinessReportOutcomeHeaderMenu
            businessReportOutcome={businessReportOutcome}
            canShare={isOwner}
            onViewEvent={canViewEvent ? handleViewEvents : undefined}
            onCreateEvent={canCreateEvent ? onCreateEventClick : undefined}
          />
        )}
      </div>
    </header>
  );
};
